import type { ArcDetails, ArcEdge, ArcEvent } from '@common/schemas/arc-details.schema';
import type { Arc } from '@common/schemas/arc.schema';
import type { Chapter } from '@common/schemas/chapter.schema';
import type { ContentDocument, ContentSummary } from '@common/schemas/content.schema';
import type { Character } from '@common/schemas/entities/character.schema';
import type { Codex } from '@common/schemas/entities/codex.schema';
import type { Event } from '@common/schemas/entities/event.schema';
import type { Location } from '@common/schemas/entities/location.schema';
import type { ImportSession } from '@common/schemas/import-session.schema';
import type { Note, NoteSummary } from '@common/schemas/notes.schema';
import type { Organization } from '@common/schemas/entities/organization.schema';
import type { FieldType } from '@common/schemas/entities/settings-field.schema';
import type {
  Audit,
  Entity,
  FieldDefinition,
  ImageRef,
  ProcessedImage,
  SectionDefinition,
} from '@common/schemas/entities/shared.schema';
import type {
  EntitiesMeta,
  EntitySummary,
  EntityTypeMeta,
  EventSummary,
  NarrativeMeta,
  NarrativeSummary,
  NarrativeTypeMeta,
  NovelStorageLayout,
  StorageMode,
  TypeStorageLayout
} from '@common/schemas/entities/summary.schema';
import { NovelSchema } from '@common/schemas/novel.schema';
import type { Relationship } from '@common/schemas/relationship.schema';
import type { WriterRoomStatus } from '@common/schemas/room-status.schema';
import type { ScenePack, SceneSnapshot } from '@common/schemas/room.schema';
import type { Scene } from '@common/schemas/scene.schema';
import type { StructureSection } from '@common/schemas/structure-section.schema';
import type { StructureLayoutType, StructureTemplate } from '@common/schemas/structure-template.schema';
import type { User, CollaboratedNovel } from '@common/schemas/user.schema';
import { z } from 'zod';

export type {
  Arc,
  ArcDetails,
  ArcEdge,
  ArcEvent,
  Audit, Chapter,
  Character, Codex, ContentDocument,
  ImportSession,
  ContentSummary, EntitiesMeta,
  Entity,
  EntitySummary, EntityTypeMeta, Event, EventSummary, FieldDefinition,
  FieldType,
  ImageRef,
  Location,
  NarrativeMeta,
  NarrativeSummary, NarrativeTypeMeta,
  Note, NoteSummary,
  NovelStorageLayout,
  Organization,
  ProcessedImage,
  Relationship,
  Scene, ScenePack,
  SceneSnapshot, SectionDefinition,
  StorageMode,
  StructureLayoutType,
  StructureSection,
  StructureTemplate, TypeStorageLayout,
  User, WriterRoomStatus, CollaboratedNovel
};

export type { EntityEventChange, EventEntityChange, FieldChange } from '@common/schemas/entities/event-change.schema';
export type { SummaryEventChange } from '@common/schemas/entities/summary.schema';

export type NarrativeStatus = 'notWritten' | 'partial' | 'complete';

export type ChapterInsights = {
  characters: string[];
  locations: string[];
  organizations: string[];
  events: string[];
};

export const ENTITY_TYPES = ['characters', 'locations', 'organizations', 'events', 'codex'] as const;
export type EntityType = typeof ENTITY_TYPES[number];

export const NARRATIVE_TYPES = ['scenes', 'chapters', 'contents', 'arcs', 'storyboards', 'structureSections', 'notes'] as const;
export type NarrativeType = typeof NARRATIVE_TYPES[number];

export type SummaryType = EntityType | NarrativeType;

export type AnySummary = EntitySummary | EventSummary | NarrativeSummary | ContentSummary | NoteSummary;

export interface NovelSummaries {
  characters: Record<string, EntitySummary>;
  locations: Record<string, EntitySummary>;
  organizations: Record<string, EntitySummary>;
  events: Record<string, EventSummary>;
  codex: Record<string, EntitySummary>;
  scenes: Record<string, NarrativeSummary>;
  chapters: Record<string, NarrativeSummary>;
  contents: Record<string, ContentSummary>;
  arcs: Record<string, NarrativeSummary>;
  storyboards: Record<string, NarrativeSummary>;
  structureSections: Record<string, NarrativeSummary>;
  notes: Record<string, NoteSummary>;
  [key: string]: Record<string, AnySummary>;
}

export const EMPTY_NOVEL_SUMMARIES: NovelSummaries = {
  characters: {},
  locations: {},
  organizations: {},
  events: {},
  codex: {},
  scenes: {},
  chapters: {},
  contents: {},
  arcs: {},
  storyboards: {},
  structureSections: {},
  notes: {},
};

export type Novel = z.infer<typeof NovelSchema> & {
  summaries: NovelSummaries;
  settings: Record<string, SectionDefinition[]>;
  codexCategories: Record<string, string>;
  codexCategoryParents: Record<string, string>;
};

export const DEFAULT_NOVEL_STORAGE_LAYOUT: NovelStorageLayout = {
  entities: {
    characters: { items: 'embedded', meta: 'embedded' },
    locations: { items: 'embedded', meta: 'embedded' },
    organizations: { items: 'embedded', meta: 'embedded' },
    events: { items: 'embedded', meta: 'embedded' },
    codex: { items: 'embedded', meta: 'embedded' },
    relationships: { items: 'embedded', meta: 'embedded' },
  },
  narrative: {
    scenes: { items: 'embedded', meta: 'embedded' },
    chapters: { items: 'embedded', meta: 'embedded' },
    contents: { items: 'split', meta: 'embedded' },
    arcs: { items: 'embedded', meta: 'embedded' },
    storyboards: { items: 'embedded', meta: 'embedded' },
  },
};

export const EMPTY_NOVEL: Novel = {
  id: '',
  title: '',
  ownerId: '',
  status: 'draft',
  size: 'small',
  collaborators: {},
  readers: {},
  noteFolders: {},
  noteFolderParents: {},
  relationshipsHiddenEntityTypeIds: [],
  entityListSortDefault: 'updatedAt',
  entityListSortBy: {},
  storageLayout: DEFAULT_NOVEL_STORAGE_LAYOUT,
  entitiesMeta: {
    summaries: {},
    settings: {},
    codexCategories: {},
    codexCategoryParents: {},
  },
  narrativeMeta: {
    summaries: {
      contents: {},
    },
  },
  canonRevision: '1',
  aiSettings: { intelligenceTier: 'eco' },
  summaries: EMPTY_NOVEL_SUMMARIES,
  settings: {},
  codexCategories: {},
  codexCategoryParents: {},
};

export type AnyEntity = (Character | Location | Organization | Event | Codex) & { entityType: string };

export type PrimaryEntityType = 'characters' | 'locations' | 'organizations';

export type EnrichedEntity = Omit<AnySummary, 'eventChanges'> & Partial<Omit<Entity, 'eventChanges'>> & {
  entityType: PrimaryEntityType | 'events' | 'arcs' | 'codex';
  title?: string;
  name?: string;
  eventChanges?: unknown;
};

export type TextSummary = ContentSummary;




